import type { useGT } from 'gt-react';
import {
  ORDER_LIFECYCLE_STAGE_SEQUENCE,
  type OrderLifecycleStage,
} from '@erp/shared-types';

type Translate = ReturnType<typeof useGT>;

/**
 * Translated display labels for the rolled-up lifecycle stage, shared by the
 * list badge, the Stage filter and the review-page stepper. Mirrors the English
 * in `ORDER_LIFECYCLE_STAGE_LABELS` (`@erp/shared-types`), which stays the
 * source for non-UI consumers (CSV export, the assistant's tool output).
 */
export function orderLifecycleStageLabels(
  gt: Translate,
): Record<OrderLifecycleStage, string> {
  return {
    draft: gt('Draft', { $context: 'sales order lifecycle stage' }),
    creating_discounts: gt('Resolving Discounts', {
      $context: 'sales order lifecycle stage: discounts are being created',
    }),
    approving: gt('Approving', {
      $context: 'sales order lifecycle stage: waiting on approvals',
    }),
    ready: gt('Ready', {
      $context: 'sales order lifecycle stage: approved, about to go to the ERP',
    }),
    created: gt('Submitted', {
      $context: 'sales order lifecycle stage: the order is in the ERP',
    }),
    under_cs_review: gt('Under CS Review', {
      $context:
        'sales order lifecycle stage: every line is an exception item, Customer Service (CS) handles it',
    }),
    rejected: gt('Rejected', { $context: 'sales order lifecycle stage' }),
    superseded: gt('Superseded', {
      $context:
        'sales order lifecycle stage: replaced by a revised purchase order',
    }),
  };
}

/**
 * The list's Stage column shows the rolled-up lifecycle stage, so the filter
 * offers the same vocabulary (incl. the under-CS-review, rejected and
 * superseded branches) — server-filtered by `salesOrdersV3.list`'s `stage`
 * param. Built from the shared sequence so it can't drift from the badge.
 * Superseded drafts are hidden unless that stage is explicitly selected.
 */
export function stageFilterOptions(
  gt: Translate,
): { value: OrderLifecycleStage; label: string }[] {
  const labels = orderLifecycleStageLabels(gt);
  return [
    ...ORDER_LIFECYCLE_STAGE_SEQUENCE,
    'under_cs_review' as const,
    'rejected' as const,
    'superseded' as const,
  ].map((stage) => ({ value: stage, label: labels[stage] }));
}
